import * as React from "react";
import GridArea, { GridAreaValue } from "./GridArea";
import ReadMore from "../../components/UI/ReadMore/ReadMore";
import { absoluteURL } from "../../util/UrlHelper";
import { ThemeConsumer, ThemeProvider, DefaultTheme } from "styled-components";
import { READ_MORE_HEIGHTS } from "../../typings/grid";
import { MEDIA_TYPES } from "../../typings/media-types";
import FadeDownWrap from "../../components/UI/Animate/FadeInDown";
import FadeUpWrap from "../../components/UI/Animate/FadeInUp";

export type ROW_MARGIN = "default" | "No spacing";

export type ROW_WIDTH = "default" | "Full width";
export type ROW_ANIM = "none" | "fadeDown" | "fadeUp";

export interface RowConfig
{
    "spacing-bottom"?: ROW_MARGIN;
    "spacing-top"?: ROW_MARGIN;
    "background-image"?: MEDIA_TYPES;
    width?: ROW_WIDTH;
    readMore?: READ_MORE_HEIGHTS;
    unpublished: boolean;
}

export interface RowStyles
{
    color?: string;
    "background-color"?: string;
    "background-image"?: string;
    "animaterow"?: ROW_ANIM;
}

export interface GridRowValue
{
    label: string;
    name: string;
    areas: GridAreaValue[];
    config: RowConfig;
    styles: RowStyles;
}

export const getReadMoreHeight = (readMore: READ_MORE_HEIGHTS) =>
{
    switch (readMore)
    {
        case "large": return 500;
        case "medium": return 350;
        case "small": return 150;
        default: return 0;
    }
};

const GridRow: React.FunctionComponent<GridRowValue> = (props) =>
{
    const { name, areas, config, styles } = props;

    const getRowTheme = (theme: DefaultTheme) =>
    {
        const rowTheme = { ...theme };
        if (styles)
        {
            rowTheme.color = styles["color"] ? styles["color"] : theme.color;
            rowTheme.backgroundColor = styles["background-color"] ? styles["background-color"] : theme.backgroundColor;
        }

        return rowTheme;
    };

    const getConfigClassNames = () =>
    {
        let classNames: string[] = ["container mx-auto", "pt-8 sm:pt-12", "pb-8 sm:pb-12"];
        if (config)
        {
            classNames = [
                config.width === "Full width" ? "grid-row grid-row--w-full" : "grid-row container",
                config["spacing-top"] === "No spacing" ? "" : "pt-8 sm:pt-12",
                config["spacing-bottom"] === "No spacing" ? "" : "pb-8 sm:pb-12"
            ];
        }
        return classNames.join(" ");
    };

    const getStyles = (): React.CSSProperties =>
    {
        const cssProps: React.CSSProperties = {};
        if (styles)
        {
            cssProps.color = styles["color"];

            cssProps.backgroundColor = styles["background-color"];

            if (styles["background-image"])
            {
                cssProps.backgroundImage = `url(${absoluteURL(styles["background-image"]["url"])})`;
            }
        }
        return cssProps;
    };


    const getAreas = () =>
    {
        const gridAreas = areas.map((area, i) =>
        {
            return <GridArea key={`ak_${i}`} {...area} />;
        });
        if (config && config.readMore)
        {
            return (
                <ReadMore height={getReadMoreHeight(config.readMore)}>
                    {gridAreas}
                </ReadMore>
            );
        }
        else
        {
            return gridAreas;
        }
    };

    const renderRow = () => (
        <div style={getStyles()}>
            <div className={getConfigClassNames()}>
                <div className={name !== "BlockGridLayout_100" ? "flex flex-wrap" : ""}>
                    {getAreas()}
                </div>
            </div>
        </div>
    );

    const getAnimation = () =>
    {
        if (config && config["animaterow"])
        {
            switch (config["animaterow"])
            {
                case "fadeDown": return <FadeDownWrap>{renderRow()}</FadeDownWrap>;
                case "fadeUp": return <FadeUpWrap>{renderRow()}</FadeUpWrap>;
                default: return renderRow();
            }
        }
        return renderRow();
    };

    const getRender = () =>
    {
        if(config && config.unpublished)
        {
            return null;
        }
        return getAnimation();
    };

    return (
        <ThemeConsumer>
            {theme =>
                <ThemeProvider theme={getRowTheme(theme)}>
                    {getRender()}
                </ThemeProvider>
            }
        </ThemeConsumer>
    );
};

export default GridRow;